import { Component, Input, ViewChild, computed, input } from '@angular/core';
import { ActionSheetController, IonItemSliding, ModalController } from '@ionic/angular';
import { catchError, of } from 'rxjs';
import * as moment from 'moment';
import {
  DATETIMEFORMART,
  EventRequest,
  UnleashFeatureFlags,
} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { AttendeesStore } from 'src/app/stores/attendees.store';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { StringInfoMessageService } from 'src/app/services/string-info-message/string-info-message.service';
import { ViewEditBookingComponent } from '../view-edit-booking/view-edit-booking.component';
import { UserStore } from 'src/app/stores/user.store';
import { TranslateService } from '@ngx-translate/core';
import { UnleashService } from 'src/app/services/unleash/unleash.service';

@Component({
  selector: 'app-my-bookings-item-compact',
  templateUrl: './my-bookings-item-compact.component.html',
  styleUrls: ['./my-bookings-item-compact.component.scss'],
})
export class MyBookingsItemCompactComponent {
  calendarEvent = input<any>();
  isCheckedOut = false;
  checkInLoading: boolean = false;
  public readonly meetingEndingSoonThreshold = 15;
  public isExtendMeetingPopoverOpen = false;
  unleashFeatureFlags = UnleashFeatureFlags;
  extendMeetingAvailibility: any = {
    '15': false,
    '30': false,
    '45': false,
    '60': false,
  };
  extendMeetingAvailibilityError: string = '';
  extendMeetingAvailibilityLoading: boolean = false;
  @ViewChild('slidingItem') slidingItem: IonItemSliding;

  @Input() selectedSegment: string;

  public locale = this.dateTimeFormatService.selectedDateTimeFormat;
  extendedAttendees = computed(() =>
    this.attendeesStore.extendAttendeeInformation(this.calendarEvent().attendees)
  );
  userResponse = computed(() =>
    this.extendedAttendees().find(
      (attendee) => attendee.userPrincipalName === this.userStore.user()?.mail
    )?.response
  );

  invitedByOrganizerDisplayName = computed(() => {
    const o = this.calendarEvent()?.organizer;
    if (!o) {
      return '';
    }
    return (o.displayName || o.name || o.emailAddress || '').trim();
  });

  showInvitedByOnCard = computed(() => {
    const ev = this.calendarEvent();
    return !!ev && !ev.isOrganizer && !!this.invitedByOrganizerDisplayName();
  });

  constructor(
    private stringInfoMessageService: StringInfoMessageService,
    private dateTimeFormatService: DatetimeFormatService,
    private apiService: ApiService,
    private attendeesStore: AttendeesStore,
    private bookingsStore: BookingsStore,
    private toastService: ToastService,
    private translateService: TranslateService,
    public unleashService: UnleashService,
    private modalController: ModalController,
    private userStore: UserStore,
    private actionSheetController: ActionSheetController,
  ) {}

  buildSeriesInformation(recurrencePattern: any): string {
    return this.stringInfoMessageService.seriesEventInformationMessage(recurrencePattern, true);
  }

  cancelledPrefix = computed(() => {
    const subject: string = this.calendarEvent()?.subject ?? '';
    const index = subject.indexOf(':');
    return index !== -1 ? subject.substring(0, index + 1) : '';
  });

  cancelledBody = computed(() => {
    const subject: string = this.calendarEvent()?.subject ?? '';
    const index = subject.indexOf(':');
    return index !== -1 ? subject.substring(index + 1).trim() : subject;
  });

  checkInEnabled = computed(() => {
    const event = this.calendarEvent();
    if (!event?.place) return false;

    const eventStart = new Date(event.startTime).getTime();
    const now = Date.now();
    const diffMinutes = (eventStart - now) / 60000;

    const eventAlreadyStarted = now >= eventStart;
    const preCheckInAllowed =
      event.place?.preCheckInEnabled && diffMinutes <= event.place.preCheckInTime;
    const durationRemainingLessThanOneMinute =
      (new Date(event.endTime).getTime() - now) / 60000 <= 1;

    return (
      event.resourceStatus === 'Accepted' &&
      !event.isCancelled &&
      !event.checkedIn &&
      !event.place?.checkOutOnlyEnabled &&
      !durationRemainingLessThanOneMinute &&
      (eventAlreadyStarted || preCheckInAllowed)
    );
  });

  checkOutEnabled = computed(() => {
    const event = this.calendarEvent();
    const now = Date.now();
    const durationRemainingLessThanOneMinute =
      (new Date(event.endTime).getTime() - now) / 60000 <= 1;

    return (
      event?.isOrganizer &&
      event.resourceStatus === 'Accepted' &&
      !event.isCancelled &&
      event.checkedIn &&
      !durationRemainingLessThanOneMinute &&
      !this.isCheckedOut
    );
  });
  isMeetingEndingSoon = computed(() => {
    if (this.calendarEvent().resourceStatus !== 'Accepted') return false;
    const now = moment();
    const startTime = moment(this.calendarEvent().startTime);
    const endTime = moment(this.calendarEvent().endTime);
    if (now.isAfter(startTime)) {
      const minutes = endTime.diff(now, 'minutes') + 1;
      return (
        minutes <= this.meetingEndingSoonThreshold &&
        minutes > 0 &&
        this.calendarEvent().isOrganizer
      );
    }
    return false;
  });

  checkIn(clickEvent?: Event) {
    clickEvent?.stopPropagation();
    this.checkInLoading = true;
    const event = this.calendarEvent();

    const eventRequest = this.mapCalendarEventIntoEventRequest();
    this.apiService
      .checkIn(event.id, eventRequest)
      .pipe(
        catchError(() => {
          this.toastService.openErrorToastAsync('checkInError');
          this.checkInLoading = false;
          return of(false);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.bookingsStore.checkInBookingLocal(event);
        }
      });
  }

  checkOut(clickEvent?: Event) {
    clickEvent?.stopPropagation();
    this.checkInLoading = false;
    const event = this.calendarEvent();
    event.isLoading = true;
    const eventRequest = this.mapCalendarEventIntoEventRequest();
    this.apiService
      .checkOut(event.id, eventRequest)
      .pipe(
        catchError(() => {
          this.toastService.openErrorToastAsync('checkOutError');
          return of(false);
        })
      )
      .subscribe((res) => {
        this.isCheckedOut = !!res;
        event.isLoading = false;
      });
  }

  async showReadOnlyBookingModal() {
    const event = this.calendarEvent();
    if (event.isLoading) {
      return;
    }
    const modal: HTMLIonModalElement = await this.modalController.create({
      component: ViewEditBookingComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      handle: false,
      canDismiss: async (data, role) => role != 'gesture',
      componentProps: {
        attendees: event.attendees,
        place: event.place,
        meetingToEdit: event,
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.action === 'declined' && data?.eventId) {
      this.bookingsStore.getMyBookings();
    }
  }

  toggleExtendMeetingPopover(clickEvent?: Event) {
    clickEvent?.stopPropagation();
    this.isExtendMeetingPopoverOpen = !this.isExtendMeetingPopoverOpen;
    if (this.isExtendMeetingPopoverOpen) {
      this.extendMeetingAvailibilityLoading = true;
      this.extendMeetingAvailibilityError = '';
      if (!this.calendarEvent().place) {
        this.extendMeetingAvailibility = {
          '15': true,
          '30': true,
          '45': true,
          '60': true,
        };
        return;
      }
      const startTime = moment(this.calendarEvent().endTime)
        .add(1, 'minutes')
        .utc()
        .format(DATETIMEFORMART);
      const endTime = moment(this.calendarEvent().endTime)
        .add(1, 'hours')
        .utc()
        .format(DATETIMEFORMART);
      this.apiService
        .getAvailibilityToExtendMeeting(
          this.calendarEvent().place?.emailAddress,
          startTime,
          endTime,
          this.calendarEvent().place?.isOnPrem
        )
        .subscribe({
          next: (res) => {
            this.extendMeetingAvailibility = res;
            if (!res) {
              this.extendMeetingAvailibilityError =
                this.translateService.instant('error fetching availability');
            } else if (Object.values(res).every((value) => value === false)) {
              this.extendMeetingAvailibilityError =
                this.translateService.instant('slotsBooked');
            } else {
              this.extendMeetingAvailibilityError = null;
            }
            this.extendMeetingAvailibilityLoading = false;
          },
          error: (err) => {
            this.toastService.openErrorToastAsync(err.message);
            this.extendMeetingAvailibilityLoading = false;
          },
        });
    }
  }

  extendMeeting(minutes: number) {
    this.extendMeetingAvailibilityLoading = true;
    const newEndTime = moment(this.calendarEvent().endTime)
      .add(minutes, 'minutes')
      .format(DATETIMEFORMART);
    this.bookingsStore
      .editBooking(
        this.calendarEvent().id,
        false,
        this.mapCalendarEventIntoEventRequest(newEndTime)
      )
      .pipe(
        catchError((error) => {
          this.toastService.openErrorToastAsync(error.error.message);
          return of(false);
        })
      )
      .subscribe(() => {
        this.isExtendMeetingPopoverOpen = false;
        this.extendMeetingAvailibilityLoading = false;
      });
  }

  async acceptEvent(eventId: string, isSeriesAction = false) {
    this.slidingItem?.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('acceptSeriesMeetingQuestion')
        : this.translateService.instant('acceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.acceptInvitation(eventId, isSeriesAction).subscribe({
        error: (ex) => this.toastService.openErrorToastAsync(ex.error.message),
      });
    }
  }

  async tentativelyAcceptEvent(eventId: string, isSeriesAction = false) {
    this.slidingItem?.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('tentativelyAcceptSeriesMeetingQuestion')
        : this.translateService.instant('tentativelyAcceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore
        .tentativelyAcceptInvitation(eventId, isSeriesAction)
        .subscribe({
          error: (ex) => this.toastService.openErrorToastAsync(ex.error.message),
        });
    }
  }

  async declineEvent(eventId: string, isSeriesAction = false) {
    this.slidingItem?.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('declineSeriesMeetingQuestion')
        : this.translateService.instant('declineMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.declineInvitation(eventId, isSeriesAction).subscribe({
        error: (ex) => this.toastService.openErrorToastAsync(ex.error.message),
      });
    }
  }

  async doDeleteBooking(id: string, isSeriesAction = false) {
    this.slidingItem?.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('deleteSeriesBookingQuestion')
        : this.translateService.instant('deleteBookingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: isSeriesAction
            ? this.translateService.instant('deleteSeriesBooking')
            : this.translateService.instant('deleteBooking'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('cancelButton'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.deleteBooking(id, isSeriesAction).subscribe({
        error: (ex) => this.toastService.openErrorToastAsync(ex.error.message),
      });
    }
  }

  async chooseIfSeriesToast(type: 'delete' | 'accept' | 'tentativelyAccept' | 'decline') {
    this.slidingItem?.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: this.translateService.instant(`series.${type}SeriesEventHeaderModal`),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('series.EventOccurrence'),
          role: 'occurrence',
        },
        {
          text: this.translateService.instant('series.EventSeries'),
          role: 'series',
        },
        {
          text: this.translateService.instant('cancelButton'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    this.chooseMatchingAction(type, condition.role);
  }

  private chooseMatchingAction(type: 'delete' | 'accept' | 'tentativelyAccept' | 'decline', role: string) {
    const event = this.calendarEvent();
    switch (type) {
      case 'delete':
        if (role === 'occurrence') this.doDeleteBooking(event.id, false);
        if (role === 'series') this.doDeleteBooking(event.seriesMasterId, true);
        break;
      case 'accept':
        if (role === 'occurrence') this.acceptEvent(event.id, false);
        if (role === 'series') this.acceptEvent(event.seriesMasterId, true);
        break;
      case 'tentativelyAccept':
        if (role === 'occurrence') this.tentativelyAcceptEvent(event.id, false);
        if (role === 'series') this.tentativelyAcceptEvent(event.seriesMasterId, true);
        break;
      case 'decline':
        if (role === 'occurrence') this.declineEvent(event.id, false);
        if (role === 'series') this.declineEvent(event.seriesMasterId, true);
        break;
      default:
        break;
    }
  }

  private mapCalendarEventIntoEventRequest(newEndTime: string = null): EventRequest {
    const event = this.calendarEvent();
    const recurrancePattern =
      event.recurrencePattern?.accuranceType == null
        ? null
        : event.recurrencePattern;

    return {
      showAs: event.showAs,
      subject: event.subject,
      startTime: moment(event.startTime).format(DATETIMEFORMART),
      endTime: newEndTime ?? moment(event.endTime).format(DATETIMEFORMART),
      isOnPrem: event.place?.isOnPrem,
      attendees: this.attendeesStore.extendAttendeeInformation(event.attendees),
      placeEmailAddress: event.place?.emailAddress,
      recurrencePattern: recurrancePattern,
      reminderMinutesBeforeStart: event.reminderMinutesBeforeStart,
      isOnlineMeeting: event.isOnlineMeeting,
      timeZoneInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }
}
